import { useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { MessageCircle } from 'lucide-react';
import { Nav } from '@/marketing/blocks/nav';
import { Footer } from '@/marketing/blocks/footer';
import { Section } from '@/marketing/blocks/Section';
import { useCopy } from '@/i18n';
import { Turnstile } from '@marsidev/react-turnstile';

type ContactCopy = {
  eyebrow: string;
  title: string;
  lead: string;
  name: string;
  email: string;
  topic: string;
  topics: Record<string, string>;
  message: string;
  submit: string;
  success: string;
  queued: string;
  error: string;
};

type Status = 'idle' | 'submitting' | 'success' | 'queued' | 'error';

const TOPIC_KEYS = ['general', 'billing', 'content', 'partnership', 'bug'] as const;

const fieldStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--paper)',
  border: '1px solid var(--line-strong)',
  borderRadius: 12,
  padding: '12px 16px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'inherit',
};

// Contact page adapted from reactbits-pro contact-6: a single centered card —
// with the real form inside (stores via POST /api/contact + SES notify).
export default function Contact() {
  const copy = useCopy();
  const c = copy<ContactCopy>('contact_page');
  const reduceMotion = useReducedMotion() ?? false;

  const [form, setForm] = useState({ name: '', email: '', topic: 'general', message: '' });
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>('idle');

  if (!c) return null;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!turnstileToken && import.meta.env.VITE_TURNSTILE_SITEKEY) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...form, turnstileToken }),
      });
      setStatus(res.status === 201 || res.ok ? 'success' : 'error');
    } catch {
      // Offline: the service worker's Background Sync queue replays this POST.
      setStatus(navigator.onLine ? 'error' : 'queued');
    }
  }

  const doneMessage = status === 'success' ? c.success : status === 'queued' ? c.queued : null;

  return (
    <>
      <Nav ctaPrimary="" />
      <Section id="contact" bg="paper">
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.3 }}
          className="mx-auto flex w-full max-w-xl flex-col items-center rounded-3xl p-8 text-center sm:p-12"
          style={{ background: 'var(--paper-2)', border: '1px solid var(--line)' }}
        >
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full shadow-sm"
            style={{ background: 'var(--coral)', color: '#fff' }}
          >
            <MessageCircle className="h-6 w-6" strokeWidth={1.75} />
          </div>

          <span className="eyebrow" style={{ marginTop: 22 }}>
            {c.eyebrow}
          </span>
          <h1 className="h-section" style={{ marginTop: 12 }}>
            {c.title}
          </h1>
          <p className="lead" style={{ marginTop: 14, fontSize: 16, maxWidth: '42ch' }}>
            {c.lead}
          </p>

          {doneMessage ? (
            <p
              role="status"
              style={{
                marginTop: 28,
                fontSize: 15,
                fontWeight: 500,
                color: 'var(--ink)',
                background: 'var(--paper)',
                border: '1px solid var(--line)',
                borderRadius: 14,
                padding: '16px 18px',
              }}
            >
              {doneMessage}
            </p>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 w-full space-y-4 text-start">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  type="text"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={c.name}
                  aria-label={c.name}
                  style={fieldStyle}
                />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={c.email}
                  aria-label={c.email}
                  style={fieldStyle}
                />
              </div>
              <select
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                aria-label={c.topic}
                style={fieldStyle}
              >
                {TOPIC_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {c.topics?.[key] ?? key}
                  </option>
                ))}
              </select>
              <textarea
                required
                maxLength={5000}
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={c.message}
                aria-label={c.message}
                style={{ ...fieldStyle, resize: 'vertical' }}
              />
              {/* Honeypot — hidden from real users */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              {status === 'error' ? (
                <p role="alert" style={{ fontSize: 13, color: 'var(--coral-2)' }}>
                  {c.error}
                </p>
              ) : null}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="btn btn-primary w-full"
                style={{ justifyContent: 'center' }}
              >
                {c.submit}
              </button>
              {import.meta.env.VITE_TURNSTILE_SITEKEY && (
                <Turnstile
                  siteKey={import.meta.env.VITE_TURNSTILE_SITEKEY}
                  options={{ size: 'invisible' }}
                  onSuccess={(token) => setTurnstileToken(token)}
                />
              )}
            </form>
          )}
        </motion.div>
      </Section>
      <Footer />
    </>
  );
}
